import { useDemoModeContext } from '@/contexts/DemoModeContext';
import { mask, type MaskType } from '@/lib/demo-mode';

/**
 * Hook for accessing demo mode state and masking values
 */
export function useDemoMode() {
  const { isDemoMode, toggleDemoMode, canAccessDemoMode } = useDemoModeContext();

  /**
   * Mask a value if demo mode is enabled
   */
  function masked<T extends string | number | null | undefined>(
    value: T,
    type: MaskType,
    options?: { showDecimals?: boolean }
  ): string {
    if (!isDemoMode) {
      return String(value ?? '');
    }
    return mask(value, type, options);
  }

  /**
   * Convenience methods for common mask types
   */
  const maskCurrency = (value: number | string | null | undefined, showDecimals = true) =>
    masked(value, 'currency', { showDecimals });

  const maskName = (value: string | null | undefined) => masked(value, 'name');

  const maskClientName = (value: string | null | undefined) => masked(value, 'clientName');

  const maskProjectName = (value: string | null | undefined) => masked(value, 'projectName');

  const maskEmail = (value: string | null | undefined) => masked(value, 'email');

  const maskUrl = (value: string | null | undefined) => masked(value, 'url');

  const maskHours = (value: number | string | null | undefined) => masked(value, 'hours');

  const maskPhone = (value: string | null | undefined) => masked(value, 'phone');

  const maskAddress = (value: string | null | undefined) => masked(value, 'address');

  const softenAlert = (value: string | null | undefined) => masked(value, 'alertText');

  return {
    isDemoMode,
    toggleDemoMode,
    canAccessDemoMode,
    masked,
    maskCurrency,
    maskName,
    maskClientName,
    maskProjectName,
    maskEmail,
    maskUrl,
    maskHours,
    maskPhone,
    maskAddress,
    softenAlert,
  };
}
